import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Box, Image, Text } from '@chakra-ui/react'
import { PaymentProvider } from '@leid/api-payments'
import Router from 'next/router'
import { ButtonWhite } from 'components/ui/Buttons/Button'
import { Loader } from 'components/ui/Loader'
import apiClient from 'helpers/clients/apiClient'
import { displaySnackbar } from 'store/partials/snackbar/snackbarSlice'

type Props = {
  isReconciliation: boolean
  orderId: number
  orderItemId: number | undefined
  purchaseAs: string
  isZeroPayment: boolean
  isLoading: boolean
  invoiceAllowed: boolean
}

export const PayButtons: React.FC<Props> = ({
  isReconciliation,
  orderId,
  orderItemId,
  purchaseAs,
  isZeroPayment,
  isLoading,
}) => {
  const { t } = useTranslation(['cart'])
  const dispatch = useDispatch()

  const [disableZeroPayButton, setDisableZeroPayButton] = useState(false)

  const isProd = process.env.APP_ENV === 'production' || process.env.APP_ENV === 'prod'

  const getPayWithVippsUrl = () => {
    const productType = isProd ? 'payex.vipps' : 'vipps'
    return getDinteroUrl(productType)
  }

  const getPayWithCardUrl = () => {
    const productType = 'payex.creditcard'
    return getDinteroUrl(productType)
  }

  const getDinteroUrl = (productType: string): string => {
    let url = `/payment/dintero?orderId=${orderId}`
    if (orderItemId) {
      url += `&orderItemId=${orderItemId}`
    }
    url += `&productType=${productType}&purchaseAs=${purchaseAs}`
    if (isReconciliation) {
      url += `&reconciliation=${isReconciliation}`
    }
    return url
  }

  const getPayWithKlarnaUrl = () => {
    const productType = 'klarna.klarna'
    return getDinteroUrl(productType)
  }

  // const getPayWithInvoiceUrl = () => {
  //   let url = `/payment/invoice?orderId=${orderId}`
  //   if (orderItemId) {
  //     url += `&orderItemId=${orderItemId}`
  //   }
  //   url += `&purchaseAs=${purchaseAs}`

  //   if (isReconciliation) {
  //     url += `&reconciliation=${isReconciliation}`
  //   }
  //   return url
  // }

  const makeZeroPayment = async () => {
    setDisableZeroPayButton(true)

    const response = await apiClient.createPayment(orderId, {
      provider: PaymentProvider.DINTERO,
      isReconciliation: isReconciliation,
      orderItemId: orderItemId,
      purchasedAs: purchaseAs,
    })

    if (!response.success) {
      // rollbar.critical('Cannot create payment for zero payment', response)
      dispatch(displaySnackbar({ message: t('error'), isError: true }))
      setDisableZeroPayButton(false)
      return
    }

    if (!response.data?.redirectUrl) {
      // rollbar.critical(`Zero payment didn't returned redirectUrl`, response)
      dispatch(displaySnackbar({ message: t('error'), isError: true }))
      setDisableZeroPayButton(false)
      return
    }

    Router.push(response.data.redirectUrl)
  }

  if (isLoading) {
    return <></>
  }

  if (isZeroPayment) {
    return (
      <>
        <Box width="100%" marginTop="25px">
          <ButtonWhite onClick={makeZeroPayment} isDisabled={disableZeroPayButton}>
            <Box width="100%" display="flex" alignItems="center" justifyContent="space-evenly">
              {disableZeroPayButton ? <Loader></Loader> : <Text>{t('payButton')}</Text>}
            </Box>
          </ButtonWhite>
        </Box>
      </>
    )
  }

  return (
    <>
      <Box width="100%" marginTop="25px">
        <ButtonWhite href={getPayWithVippsUrl()}>
          <Box width="100%" display="flex" alignItems="center" justifyContent="space-evenly">
            <Text>{t('payWithVipps')}</Text>
            <Image
              pointerEvents="none"
              display="block"
              src="/icons/vipps-smiling-rgb-orange-pos.svg"
              alt="Vipps"
              width="64px"
            />
          </Box>
        </ButtonWhite>
      </Box>
      <Box width="100%" marginTop="25px">
        <ButtonWhite href={getPayWithCardUrl()}>
          <Box width="100%" display="flex" alignItems="center" justifyContent="space-evenly">
            <Text>{t('payWithCard')}</Text>
            <Image pointerEvents="none" display="block" src="/icons/creditcard.svg" alt="Vipps" width="64px" />
          </Box>
        </ButtonWhite>
      </Box>

      <Box width="100%" marginTop="25px">
        <ButtonWhite href={getPayWithKlarnaUrl()}>
          <Box width="100%" display="flex" alignItems="center" justifyContent="space-evenly">
            <Text>{t('payWithKlarnaButton')}</Text>
            <Image pointerEvents="none" display="block" src="/icons/klarna.svg" alt="Klarna" width="64px" />
          </Box>
        </ButtonWhite>
      </Box>

      {/* We can't allow invoices, because then we can't do correct Split inside of Dintero between us and involved managers */}
      {/* <Box width="100%" marginTop="25px">
        {invoiceAllowed && (
          <ButtonWhite href={getPayWithInvoiceUrl()}>
            <Box width="100%" display="flex" alignItems="center" justifyContent="space-evenly">
              <Text>{t('payWithInvoice')}</Text>              
            </Box>
          </ButtonWhite>
        )}
      </Box> */}
    </>
  )
}
